import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sunrise, X, Volume2, Square, Crown } from "lucide-react";
import { speak, stopSpeaking, briefingToText, speechSupported } from "@/lib/speak";

const todayKey = () => new Date().toISOString().slice(0, 10);

export default function BriefingPopup({ onOpenBoss }) {
  const [briefing, setBriefing] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("lifeos_briefing_shown") === todayKey()) return;
    let cancelled = false;
    api.bossBriefing().then((b) => {
      if (cancelled) return;
      if (b && (b.greeting || b.decisions?.length)) {
        setBriefing(b);
        localStorage.setItem("lifeos_briefing_shown", todayKey());
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const close = () => { stopSpeaking(); setBriefing(null); };
  const toggleSpeak = () => {
    if (speaking) { stopSpeaking(); setSpeaking(false); return; }
    speak(briefingToText(briefing), { onStart: () => setSpeaking(true), onEnd: () => setSpeaking(false) });
  };
  const goBoss = () => { close(); onOpenBoss?.(); };

  if (!briefing) return null;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-4 anim-fadein" data-testid="briefing-popup" onClick={close}>
      <div className="w-full max-w-md rounded-2xl border border-amber-400/30 bg-gradient-to-br from-[#0d1526] to-[#120e08] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <span className="grid place-items-center rounded-lg" style={{ width: 36, height: 36, background: "#ffd45a1a", border: "1px solid #ffd45a44" }}><Sunrise size={18} className="text-amber-300" /></span>
          <h2 className="font-display uppercase tracking-widest text-amber-300 text-sm">Morning Briefing</h2>
          {speechSupported() && (
            <button data-testid="briefing-speak" onClick={toggleSpeak} title={speaking ? "Stop" : "Read aloud"} className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2.5 h-8 border border-white/10 text-zinc-200 hover:border-amber-400/40">
              {speaking ? <Square size={13} /> : <Volume2 size={14} />} {speaking ? "Stop" : "Play"}
            </button>
          )}
          <button data-testid="briefing-close" onClick={close} className="text-zinc-500 hover:text-white p-1"><X size={16} /></button>
        </div>

        <p className="text-sm text-zinc-100 font-medium">{briefing.greeting}</p>
        {briefing.decisions?.length > 0 && (
          <ol className="mt-4 space-y-2.5">
            {briefing.decisions.map((d, i) => (
              <li key={i} className="flex gap-3" data-testid="briefing-popup-decision">
                <span className="grid place-items-center shrink-0 w-6 h-6 rounded-full bg-amber-400/15 text-amber-300 text-[11px] font-bold font-mono mt-0.5">{i + 1}</span>
                <div><p className="text-sm text-zinc-100 font-medium">{d.title}</p><p className="text-xs text-zinc-500">{d.why}</p></div>
              </li>
            ))}
          </ol>
        )}
        {briefing.closing && <p className="text-xs text-amber-200/70 mt-4 italic">{briefing.closing}</p>}

        <div className="flex items-center gap-2 mt-6">
          <button data-testid="briefing-go-boss" onClick={goBoss} className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-lg h-10">
            <Crown size={14} /> Open AI Boss
          </button>
          <button data-testid="briefing-dismiss" onClick={close} className="text-xs text-zinc-400 hover:text-zinc-200 px-4 h-10 border border-white/10 rounded-lg">Later</button>
        </div>
      </div>
    </div>
  );
}
